/**
 * DataStore - Centralized data management for OWLY CRM
 * Manages all entities with proper relationships and indexing
 * Developed by OWLY Labs
 */

import {
  ExtendedLead,
  ExtendedClient,
  ExtendedUnit,
  Activity,
  TimelineEvent,
  DateRange,
  QueryOptions,
  CalculatedMetrics,
  ExtendedCalculatedMetrics,
  ExecutiveMetrics,
  AgentPerformance,
  StrategicGoal,
  FinancialPeriod,
  CostStructure,
  Agent,
  ProjectFinancials,
  ProjectFinancialPeriod,
  AuditLogEntry
} from './types';

class DataStore {
  // Main data stores
  private leads: Map<string, ExtendedLead> = new Map();
  private clients: Map<string, ExtendedClient> = new Map();
  private units: Map<string, ExtendedUnit> = new Map();
  private activities: Map<string, Activity> = new Map();
  private agents: Map<string, Agent> = new Map();
  private goals: Map<string, StrategicGoal> = new Map();
  private periods: Map<string, FinancialPeriod> = new Map(); // 'YYYY-MM' -> period
  private projects: Map<string, ProjectFinancials> = new Map(); // 🆕 Project Structuring

  // Indexes for fast queries
  private leadsByStage: Map<number, Set<string>> = new Map();
  private clientsByPhase: Map<string, Set<string>> = new Map();
  private unitsByProject: Map<string, Set<string>> = new Map();
  private activitiesByEntity: Map<string, Set<string>> = new Map(); // entityType:entityId -> activity ids
  private activitiesByAgent: Map<string, Set<string>> = new Map(); // agentId -> activity ids
  private leadsByAgent: Map<string, Set<string>> = new Map(); // agentId -> lead ids
  private clientsByAgent: Map<string, Set<string>> = new Map(); // agentId -> client ids

  // Relationship mappings (bidirectional)
  private leadToClient: Map<string, string> = new Map(); // leadId -> clientId
  private clientToLead: Map<string, string> = new Map(); // clientId -> leadId
  private leadToUnits: Map<string, Set<string>> = new Map(); // leadId -> unitIds
  private unitToLead: Map<string, string> = new Map(); // unitId -> leadId
  private unitToClient: Map<string, string> = new Map(); // unitId -> clientId

  // ============================================================================
  // LEAD OPERATIONS
  // ============================================================================

  addLead(lead: ExtendedLead): void {
    this.leads.set(lead.id, lead);
    this.indexLead(lead);

    // Index activities
    lead.activities?.forEach(activity => {
      this.activities.set(activity.id, activity);
      this.indexActivity(activity, 'lead', lead.id);
    });
  }

  getLead(id: string): ExtendedLead | undefined {
    return this.leads.get(id);
  }

  getAllLeads(): ExtendedLead[] {
    return Array.from(this.leads.values());
  }

  getLeadsByStage(stage: number): ExtendedLead[] {
    const ids = this.leadsByStage.get(stage) || new Set();
    return Array.from(ids).map(id => this.leads.get(id)!).filter(Boolean);
  }

  updateLead(id: string, updates: Partial<ExtendedLead>): void {
    const lead = this.leads.get(id);
    if (!lead) return;

    // Reindex if stage changed
    if (updates.stage !== undefined && updates.stage !== lead.stage) {
      this.removeLeadFromIndex(lead);
    }

    const updatedLead = { ...lead, ...updates };
    this.leads.set(id, updatedLead);

    if (updates.stage !== undefined) {
      this.indexLead(updatedLead);
    }
  }

  private indexLead(lead: ExtendedLead): void {
    // Index by stage
    if (!this.leadsByStage.has(lead.stage)) {
      this.leadsByStage.set(lead.stage, new Set());
    }
    this.leadsByStage.get(lead.stage)!.add(lead.id);

    // Index unit interests
    lead.interestedUnits?.forEach(interest => {
      if (!this.leadToUnits.has(lead.id)) {
        this.leadToUnits.set(lead.id, new Set());
      }
      this.leadToUnits.get(lead.id)!.add(interest.unitId);
      this.unitToLead.set(interest.unitId, lead.id);
    });
  }

  private removeLeadFromIndex(lead: ExtendedLead): void {
    const stageSet = this.leadsByStage.get(lead.stage);
    if (stageSet) {
      stageSet.delete(lead.id);
    }
  }

  // ============================================================================
  // CLIENT OPERATIONS
  // ============================================================================

  addClient(client: ExtendedClient): void {
    this.clients.set(client.id, client);
    this.indexClient(client);

    // Index activities
    client.activities?.forEach(activity => {
      this.activities.set(activity.id, activity);
      this.indexActivity(activity, 'client', client.id);
    });

    // Update bidirectional Lead ↔ Client relationship
    if (client.originLeadId) {
      this.leadToClient.set(client.originLeadId, client.id);
      this.clientToLead.set(client.id, client.originLeadId);

      // Update lead with conversion info
      const lead = this.leads.get(client.originLeadId);
      if (lead) {
        lead.convertedToClientId = client.id;
        lead.conversionDate = client.closingDate;
      }
    }
  }

  getClient(id: string): ExtendedClient | undefined {
    return this.clients.get(id);
  }

  getAllClients(): ExtendedClient[] {
    return Array.from(this.clients.values());
  }

  getClientsByPhase(phase: string): ExtendedClient[] {
    const ids = this.clientsByPhase.get(phase) || new Set();
    return Array.from(ids).map(id => this.clients.get(id)!).filter(Boolean);
  }

  getClientsInRisk(): ExtendedClient[] {
    return Array.from(this.clients.values())
      .filter(c => c.scoring?.riskLevel === 'high' || c.scoring?.riskLevel === 'critical');
  }

  private indexClient(client: ExtendedClient): void {
    // Index by phase
    if (!this.clientsByPhase.has(client.currentPhase)) {
      this.clientsByPhase.set(client.currentPhase, new Set());
    }
    this.clientsByPhase.get(client.currentPhase)!.add(client.id);
  }

  // ============================================================================
  // UNIT OPERATIONS
  // ============================================================================

  addUnit(unit: ExtendedUnit): void {
    this.units.set(unit.id, unit);
    this.indexUnit(unit);

    // Update relationships
    if (unit.leadId) {
      this.unitToLead.set(unit.id, unit.leadId);
    }
    if (unit.clientId) {
      this.unitToClient.set(unit.id, unit.clientId);
    }
  }

  getUnit(id: string): ExtendedUnit | undefined {
    return this.units.get(id);
  }

  getUnitsByProject(projectId: string): ExtendedUnit[] {
    const ids = this.unitsByProject.get(projectId) || new Set();
    return Array.from(ids).map(id => this.units.get(id)!).filter(Boolean);
  }

  getAvailableUnits(): ExtendedUnit[] {
    return Array.from(this.units.values()).filter(u => u.status === 'available');
  }

  private indexUnit(unit: ExtendedUnit): void {
    // Index by project
    if (!this.unitsByProject.has(unit.projectId)) {
      this.unitsByProject.set(unit.projectId, new Set());
    }
    this.unitsByProject.get(unit.projectId)!.add(unit.id);
  }

  // ============================================================================
  // ACTIVITY OPERATIONS
  // ============================================================================

  addActivity(activity: Activity): void {
    this.activities.set(activity.id, activity);
    this.indexActivity(activity, activity.entityType, activity.entityId);

    // Add to entity's activities array
    if (activity.entityType === 'lead') {
      const lead = this.leads.get(activity.entityId);
      if (lead) {
        if (!lead.activities) lead.activities = [];
        lead.activities.push(activity);
      }
    } else if (activity.entityType === 'client') {
      const client = this.clients.get(activity.entityId);
      if (client) {
        if (!client.activities) client.activities = [];
        client.activities.push(activity);
      }
    }
  }

  getActivitiesByEntity(entityType: 'lead' | 'client', entityId: string): Activity[] {
    const key = `${entityType}:${entityId}`;
    const ids = this.activitiesByEntity.get(key) || new Set();
    return Array.from(ids).map(id => this.activities.get(id)!).filter(Boolean);
  }

  private indexActivity(activity: Activity, entityType: string, entityId: string): void {
    const key = `${entityType}:${entityId}`;
    if (!this.activitiesByEntity.has(key)) {
      this.activitiesByEntity.set(key, new Set());
    }
    this.activitiesByEntity.get(key)!.add(activity.id);
  }

  // ============================================================================
  // CONVERSION & RELATIONSHIPS
  // ============================================================================

  convertLeadToClient(leadId: string, clientData: Partial<ExtendedClient>): ExtendedClient {
    const lead = this.leads.get(leadId);
    if (!lead) throw new Error(`Lead ${leadId} not found`);

    const client: ExtendedClient = {
      id: clientData.id || `client-${Date.now()}`,
      name: lead.name,
      email: lead.perfil.email,
      phone: lead.perfil.telefono,
      avatar: (lead as any).avatar,

      // Lead relationship
      originLeadId: leadId,
      originalLeadId: leadId,
      leadCreatedAt: lead.perfil.fechaContacto,
      daysToConversion: this.calculateDaysToConversion(lead.perfil.fechaContacto, new Date().toISOString()),

      closingDate: new Date().toISOString().split('T')[0],
      salesPerson: 'current-user', // Should come from context

      // Default values (to be overridden by clientData)
      propertyInfo: clientData.propertyInfo || {} as any,
      dates: clientData.dates || {} as any,
      currentPhase: 'cuotas-iniciales',
      progress: 0,

      financial: clientData.financial || {} as any,
      construction: clientData.construction || {} as any,
      communication: clientData.communication || {} as any,
      deliverables: clientData.deliverables || {} as any,

      // Extended fields
      activities: [],
      timeline: [],
      scoring: {
        total: 0,
        paymentCompliance: 0,
        engagement: 0,
        satisfaction: 0,
        riskLevel: 'low',
        documentation: 0
      },
      support: { tickets: [] },
      satisfaction: { surveys: [] },

      ...clientData
    };

    this.addClient(client);

    // Update lead
    lead.convertedToClientId = client.id;
    lead.conversionDate = client.closingDate;

    return client;
  }

  getClientByLeadId(leadId: string): ExtendedClient | undefined {
    const clientId = this.leadToClient.get(leadId);
    return clientId ? this.clients.get(clientId) : undefined;
  }

  getLeadByClientId(clientId: string): ExtendedLead | undefined {
    const leadId = this.clientToLead.get(clientId);
    return leadId ? this.leads.get(leadId) : undefined;
  }

  private calculateDaysToConversion(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }

  // ============================================================================
  // METRICS & CALCULATIONS
  // ============================================================================

  calculateConversionRate(period?: DateRange): number {
    const leads = period ? this.getLeadsInPeriod(period) : this.getAllLeads();
    const converted = leads.filter(l => l.convertedToClientId);
    return leads.length > 0 ? (converted.length / leads.length) * 100 : 0;
  }

  calculatePipelineValue(): number {
    const probabilityByStage: Record<number, number> = {
      1: 0.1, // Contacto
      2: 0.2, // Calificación
      3: 0.3, // Presentación
      4: 0.5, // Propuesta
      5: 0.7, // Negociación
      6: 0.9, // Cierre
      7: 1.0, // Ganado
      8: 0.0  // Perdido
    };

    return Array.from(this.leads.values())
      .reduce((sum, lead) => {
        const prob = probabilityByStage[lead.stage] || 0;
        return sum + (lead.value * prob);
      }, 0);
  }

  getMetrics(): CalculatedMetrics {
    const allLeads = this.getAllLeads();
    const allClients = this.getAllClients();
    const allActivities = Array.from(this.activities.values());

    // Lead metrics by stage
    const leadsByStage: Record<number, number> = {};
    for (let i = 1; i <= 8; i++) {
      leadsByStage[i] = this.getLeadsByStage(i).length;
    }

    // Client metrics by phase
    const clientsByPhase: Record<string, number> = {};
    const phases = ['cuotas-iniciales', 'documentacion', 'vinculacion-fiducia', 'escrituracion',
                    'financiacion', 'construccion', 'pre-entrega', 'entrega'];
    phases.forEach(phase => {
      clientsByPhase[phase] = this.getClientsByPhase(phase).length;
    });

    // Activity metrics by type
    const activitiesByType: Record<string, number> = {};
    allActivities.forEach(activity => {
      activitiesByType[activity.type] = (activitiesByType[activity.type] || 0) + 1;
    });

    // Calculate averages
    const avgSatisfaction = allClients.length > 0
      ? allClients.reduce((sum, c) => sum + (c.satisfaction?.nps || 0), 0) / allClients.length
      : 0;

    const totalRevenue = allClients.reduce((sum, c) => sum + c.financial.totalValue, 0);

    const avgTimeToClose = this.calculateAvgTimeToClose();

    return {
      leads: {
        total: allLeads.length,
        byStage: leadsByStage,
        conversionRate: this.calculateConversionRate(),
        avgTimeToClose,
        pipelineValue: this.calculatePipelineValue()
      },
      clients: {
        total: allClients.length,
        byPhase: clientsByPhase,
        atRisk: this.getClientsInRisk().length,
        avgSatisfaction,
        totalRevenue
      },
      activities: {
        total: allActivities.length,
        byType: activitiesByType as any,
        avgPerLead: allLeads.length > 0 ? allActivities.filter(a => a.entityType === 'lead').length / allLeads.length : 0,
        avgPerClient: allClients.length > 0 ? allActivities.filter(a => a.entityType === 'client').length / allClients.length : 0
      }
    };
  }

  private calculateAvgTimeToClose(): number {
    const convertedClients = this.getAllClients().filter(c => c.originLeadId);
    if (convertedClients.length === 0) return 0;

    const totalDays = convertedClients.reduce((sum, client) => sum + client.daysToConversion, 0);
    return totalDays / convertedClients.length;
  }

  private getLeadsInPeriod(period: DateRange): ExtendedLead[] {
    return Array.from(this.leads.values()).filter(lead => {
      const createdDate = new Date(lead.perfil.fechaContacto);
      return createdDate >= period.start && createdDate <= period.end;
    });
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  clear(): void {
    this.leads.clear();
    this.clients.clear();
    this.units.clear();
    this.activities.clear();
    this.agents.clear();
    this.goals.clear();
    this.periods.clear();
    this.leadsByStage.clear();
    this.clientsByPhase.clear();
    this.unitsByProject.clear();
    this.activitiesByEntity.clear();
    this.activitiesByAgent.clear();
    this.leadsByAgent.clear();
    this.clientsByAgent.clear();
    this.leadToClient.clear();
    this.clientToLead.clear();
    this.leadToUnits.clear();
    this.unitToLead.clear();
    this.unitToClient.clear();
  }

  leadExists(id: string): boolean {
    return this.leads.has(id);
  }

  clientExists(id: string): boolean {
    return this.clients.has(id);
  }

  unitExists(id: string): boolean {
    return this.units.has(id);
  }

  getStats() {
    return {
      leads: this.leads.size,
      clients: this.clients.size,
      units: this.units.size,
      activities: this.activities.size,
      agents: this.agents.size,
      goals: this.goals.size,
      relationships: {
        leadToClient: this.leadToClient.size,
        leadToUnits: this.leadToUnits.size
      }
    };
  }

  // ============================================================================
  // AGENT OPERATIONS
  // ============================================================================

  addAgent(agent: Agent): void {
    this.agents.set(agent.id, agent);
  }

  getAgent(id: string): Agent | undefined {
    return this.agents.get(id);
  }

  getAllAgents(): Agent[] {
    return Array.from(this.agents.values());
  }

  getActiveAgents(): Agent[] {
    return Array.from(this.agents.values()).filter(a => a.isActive);
  }

  // ============================================================================
  // GOAL OPERATIONS
  // ============================================================================

  addGoal(goal: StrategicGoal): void {
    this.goals.set(goal.id, goal);
  }

  getGoal(id: string): StrategicGoal | undefined {
    return this.goals.get(id);
  }

  getAllGoals(): StrategicGoal[] {
    return Array.from(this.goals.values());
  }

  updateGoalProgress(goalId: string, current: number): void {
    const goal = this.goals.get(goalId);
    if (goal) {
      goal.current = current;
      goal.progress = (current / goal.target) * 100;

      // Update status based on progress
      if (goal.progress >= 100) {
        goal.status = 'completed';
        goal.completedDate = new Date().toISOString().split('T')[0];
      } else if (goal.progress >= 70) {
        goal.status = 'on-track';
      } else if (goal.progress >= 50) {
        goal.status = 'at-risk';
      } else {
        goal.status = 'behind';
      }
    }
  }

  // ============================================================================
  // FINANCIAL PERIOD OPERATIONS
  // ============================================================================

  addPeriod(period: FinancialPeriod): void {
    this.periods.set(period.period, period);
  }

  getPeriod(period: string): FinancialPeriod | undefined {
    return this.periods.get(period);
  }

  getAllPeriods(): FinancialPeriod[] {
    return Array.from(this.periods.values()).sort((a, b) =>
      b.period.localeCompare(a.period)
    );
  }

  getLastNPeriods(n: number): FinancialPeriod[] {
    return this.getAllPeriods().slice(0, n);
  }

  // ============================================================================
  // AGENT PERFORMANCE CALCULATIONS
  // ============================================================================

  calculateAgentPerformance(agentId: string): AgentPerformance | null {
    const agent = this.agents.get(agentId);
    if (!agent) return null;

    // Get agent's clients (assuming salesPerson field exists)
    const agentClients = Array.from(this.clients.values()).filter(
      c => (c as any).salesPerson === agentId
    );

    // Get agent's activities
    const agentActivities = Array.from(this.activities.values()).filter(
      a => a.userId === agentId
    );

    const revenue = agentClients.reduce((sum, c) => sum + c.financial.totalValue, 0);
    const deals = agentClients.length;

    // Get agent's leads
    const agentLeads = Array.from(this.leads.values()).filter(
      l => (l as any).assignedTo === agentId
    );
    const convertedLeads = agentLeads.filter(l => l.convertedToClientId).length;

    const performance: AgentPerformance = {
      agentId,
      agentName: agent.name,
      role: agent.role,
      revenue,
      deals,
      conversionRate: agentLeads.length > 0 ? (convertedLeads / agentLeads.length) * 100 : 0,
      calls: agentActivities.filter(a => a.type === 'call').length,
      meetings: agentActivities.filter(a => a.type === 'meeting').length,
      emails: agentActivities.filter(a => a.type === 'email').length,
      rank: 0, // Will be calculated when getting all performances
      targetAchievement: (revenue / agent.monthlyTarget) * 100,
      avgDealSize: deals > 0 ? revenue / deals : 0,
      avgResponseTime: 2.5, // Mock - would need timestamp tracking
      avgTimeToClose: agentClients.length > 0
        ? agentClients.reduce((sum, c) => sum + c.daysToConversion, 0) / agentClients.length
        : 0
    };

    return performance;
  }

  getAllAgentPerformances(): AgentPerformance[] {
    const performances = this.getActiveAgents()
      .map(agent => this.calculateAgentPerformance(agent.id))
      .filter(p => p !== null) as AgentPerformance[];

    // Sort by revenue and assign ranks
    performances.sort((a, b) => b.revenue - a.revenue);
    performances.forEach((p, i) => p.rank = i + 1);

    return performances;
  }

  // ============================================================================
  // EXECUTIVE METRICS CALCULATIONS
  // ============================================================================

  calculateExecutiveMetrics(): ExecutiveMetrics {
    const allClients = this.getAllClients();
    const totalRevenue = allClients.reduce((sum, c) => sum + c.financial.totalValue, 0);
    const activeAgents = this.getActiveAgents();

    // Calculate costs
    const totalPayroll = activeAgents.reduce((sum, a) => sum + a.monthlySalary, 0);
    const marketingCost = totalRevenue * 0.08; // 8% of revenue
    const operationalExpenses = totalPayroll + marketingCost;

    // Calculate profit
    const profit = totalRevenue - operationalExpenses;
    const profitMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

    // Calculate CAC and LTV
    const totalLeads = this.getAllLeads().length;
    const convertedLeads = this.getAllLeads().filter(l => l.convertedToClientId).length;
    const customerAcquisitionCost = convertedLeads > 0 ? marketingCost / convertedLeads : 0;
    const lifetimeValue = allClients.length > 0 ? totalRevenue / allClients.length : 0;

    // Calculate growth (mock data for now - would need historical periods)
    const periods = this.getLastNPeriods(2);
    let monthOverMonth = 18.5; // Default mock
    const yearOverYear = 45.2;   // Default mock

    if (periods.length >= 2) {
      const current = periods[0].revenue;
      const previous = periods[1].revenue;
      monthOverMonth = previous > 0 ? ((current - previous) / previous) * 100 : 0;
    }

    // Company health score calculation
    const salesScore = Math.min(100, (totalRevenue / 5000000000) * 100); // Target 5B
    const financeScore = Math.min(100, profitMargin * 3); // 33% margin = 100 score
    const teamScore = Math.min(100, (activeAgents.length / 50) * 100); // Target 50 agents
    const operationalScore = 78; // Mock for now
    const companyHealthScore = (salesScore * 0.35 + financeScore * 0.35 + teamScore * 0.15 + operationalScore * 0.15);

    return {
      totalRevenue,
      monthOverMonth,
      yearOverYear,
      profitMargin,
      monthlyCost: operationalExpenses,
      operationalExpenses,
      marketingCost,
      teamSize: activeAgents.length,
      avgSalary: activeAgents.length > 0 ? totalPayroll / activeAgents.length : 0,
      totalPayroll,
      customerAcquisitionCost,
      lifetimeValue,
      ltvCacRatio: customerAcquisitionCost > 0 ? lifetimeValue / customerAcquisitionCost : 0,
      churnRate: 3.2, // Mock - would need historical client tracking
      marketShare: 23.5, // Mock - would need market data
      roi: totalRevenue > 0 ? (profit / operationalExpenses) * 100 : 0,
      activeProjects: 8, // Mock - would come from projects module
      companyHealthScore: Math.round(companyHealthScore)
    };
  }

  // ============================================================================
  // EXTENDED METRICS (with Executive Data)
  // ============================================================================

  getExtendedMetrics(): ExtendedCalculatedMetrics {
    const baseMetrics = this.getMetrics();

    return {
      ...baseMetrics,
      executive: this.calculateExecutiveMetrics(),
      agents: this.getAllAgentPerformances(),
      goals: this.getAllGoals(),
      periods: this.getLastNPeriods(12),
      costs: this.calculateCostStructure()
    };
  }

  private calculateCostStructure(): CostStructure {
    const activeAgents = this.getActiveAgents();
    const salaries = activeAgents.reduce((sum, a) => sum + a.monthlySalary, 0);
    const revenue = this.getAllClients().reduce((sum, c) => sum + c.financial.totalValue, 0);

    const commissions = revenue * 0.035; // 3.5% avg commission
    const marketing = revenue * 0.08; // 8% of revenue
    const leads = marketing * 0.3; // 30% of marketing on leads

    const rent = 35000000; // 35M COP monthly
    const utilities = 5000000; // 5M COP
    const software = 8000000; // 8M COP (CRM, tools, etc)
    const insurance = 3000000; // 3M COP

    const total = salaries + rent + utilities + software + insurance + commissions + marketing + leads;

    return {
      salaries,
      rent,
      utilities,
      software,
      insurance,
      commissions,
      marketing,
      leads,
      total,
      monthly: total
    };
  }

  // ============================================================================
  // PROJECT FINANCIALS OPERATIONS (Real Estate Structuring)
  // ============================================================================

  addProject(project: ProjectFinancials): void {
    this.projects.set(project.id, project);
  }

  getProject(id: string): ProjectFinancials | undefined {
    return this.projects.get(id);
  }

  getAllProjects(): ProjectFinancials[] {
    return Array.from(this.projects.values());
  }

  /**
   * Get projects filtered by user role and permissions
   * @param userId Current user ID
   * @param userRole Current user role
   */
  getProjectsByUser(userId: string, userRole: string): ProjectFinancials[] {
    const allProjects = this.getAllProjects();

    // President sees ALL projects
    if (userRole === 'president') {
      return allProjects;
    }

    // Sales Manager sees only their projects
    if (userRole === 'sales-manager') {
      return allProjects.filter(p => p.salesManager === userId);
    }

    // Finance Manager sees only their projects
    if (userRole === 'finance-manager') {
      return allProjects.filter(p => p.financeManager === userId);
    }

    // Default: no access
    return [];
  }

  /**
   * Update project with audit trail
   * @param id Project ID
   * @param updates Partial updates to apply
   * @param userId User making the update
   * @param userRole User's role
   */
  updateProject(
    id: string,
    updates: Partial<ProjectFinancials>,
    userId: string,
    userRole: string,
    userName: string
  ): void {
    const project = this.projects.get(id);
    if (!project) return;

    // Track changes for audit log
    const changes: AuditLogEntry['changes'] = [];

    // Simple change tracking (would be more sophisticated in production)
    Object.keys(updates).forEach(key => {
      if (key !== 'updatedAt' && key !== 'auditLog') {
        const oldValue = (project as any)[key];
        const newValue = (updates as any)[key];

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push({
            field: key,
            oldValue,
            newValue
          });
        }
      }
    });

    // Apply updates
    const updatedProject = {
      ...project,
      ...updates,
      updatedAt: new Date().toISOString()
    };

    this.projects.set(id, updatedProject);

    // Add audit log entry if there were changes
    if (changes.length > 0) {
      this.addProjectAuditLog({
        id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        userId,
        userRole,
        userName,
        action: 'update',
        entity: 'project',
        entityId: id,
        changes
      }, id);
    }
  }

  deleteProject(id: string): void {
    this.projects.delete(id);
  }

  /**
   * Add audit log entry to a project
   */
  private addProjectAuditLog(entry: AuditLogEntry, projectId: string): void {
    const project = this.projects.get(projectId);
    if (!project) return;

    if (!project.auditLog) {
      project.auditLog = [];
    }

    project.auditLog.push(entry);
  }

  /**
   * Get audit log for a project
   * Filtered by user role
   */
  getProjectAuditLog(projectId: string, userRole: string): AuditLogEntry[] {
    const project = this.projects.get(projectId);
    if (!project || !project.auditLog) return [];

    // President sees ALL audit logs
    if (userRole === 'president') {
      return project.auditLog;
    }

    // Sales Manager sees only commercial-related changes
    if (userRole === 'sales-manager') {
      return project.auditLog.filter(entry => {
        if (!entry.changes) return false;
        return entry.changes.some(c => c.field.includes('commercial') || c.field.includes('sales'));
      });
    }

    // Finance Manager sees only finance-related changes
    if (userRole === 'finance-manager') {
      return project.auditLog.filter(entry => {
        if (!entry.changes) return false;
        return entry.changes.some(c =>
          c.field.includes('land') ||
          c.field.includes('financing') ||
          c.field.includes('costs.direct') ||
          c.field.includes('costs.indirect')
        );
      });
    }

    return [];
  }

  /**
   * Check if user can access a specific project
   */
  canAccessProject(projectId: string, userId: string, userRole: string): boolean {
    const project = this.projects.get(projectId);
    if (!project) return false;

    // President can access all
    if (userRole === 'president') return true;

    // Sales Manager can access their projects
    if (userRole === 'sales-manager' && project.salesManager === userId) return true;

    // Finance Manager can access their projects
    if (userRole === 'finance-manager' && project.financeManager === userId) return true;

    return false;
  }

  /**
   * Check if user can edit a specific field
   * Based on role-based permissions from documentation
   */
  canEditField(field: string, userRole: string): boolean {
    // Field permission matrix (from 01-roles-and-permissions.md)
    const fieldPermissions: Record<string, string[]> = {
      // Market Analysis
      'market.stratum': ['president', 'sales-manager'],
      'market.zonePrice': ['president', 'sales-manager'],

      // Land (Finance Manager's area)
      'land.cost': ['president', 'finance-manager'],
      'land.landArea': ['president', 'finance-manager'],

      // Commercial Costs (Sales Manager's area) 🔑
      'costs.commercial.salesCommissionPercent': ['president', 'sales-manager'],
      'costs.commercial.marketingPercent': ['president', 'sales-manager'],
      'costs.commercial.salesOfficeCost': ['president', 'sales-manager'],
      'costs.commercial.agentCommissions': ['president', 'sales-manager'],

      // Construction Costs (Finance Manager's area)
      'costs.direct.constructionCostM2': ['president', 'finance-manager'],
      'costs.indirect.contingencyPercent': ['president', 'finance-manager'],

      // Financing (Finance Manager's area)
      'financing.wacc': ['president', 'finance-manager'],
      'financing.equityPercent': ['president', 'finance-manager'],
      'financing.constructionLoanRate': ['president', 'finance-manager'],

      // Sales Parameters (Sales Manager's area)
      'sales.cancellationRate': ['president', 'sales-manager'],
      'sales.presalesVelocity': ['president', 'sales-manager'],
    };

    const allowedRoles = fieldPermissions[field];
    if (!allowedRoles) {
      // If field not in matrix, default to president only
      return userRole === 'president';
    }

    return allowedRoles.includes(userRole);
  }
}

// Singleton instance
export const dataStore = new DataStore();
export default DataStore;
